import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Checkbox, Box, Stack,
    MenuItem, Select, InputLabel, FormControl, FormHelperText,
} from '@mui/material';
import { Shift } from '../../interfaces/shift/Shift';
import { ShiftAssignment, CreateAssignmentRequest } from '../../interfaces/shift/ShiftAssignment';
import { ShiftAssignmentService } from '../../services/shift-assignment.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

interface Props {
    open: boolean;
    onClose: () => void;
    userId: string;
    userName: string;
    institutionId: string;
    shifts: Shift[];
    existingAssignment: ShiftAssignment | null;
    onSaved: () => void;
}

const today = () => new Date().toISOString().slice(0, 10);

const AssignmentEditDialog: React.FC<Props> = ({
    open, onClose, userId, userName, institutionId, shifts, existingAssignment, onSaved,
}) => {
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);
    const [shiftId, setShiftId] = useState('');
    const [effectiveFrom, setEffectiveFrom] = useState(today());
    const [effectiveTo, setEffectiveTo] = useState('');
    const [openEnded, setOpenEnded] = useState(true);
    const [replaceExisting, setReplaceExisting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<{ shiftId?: string; effectiveFrom?: string; effectiveTo?: string }>({});

    useEffect(() => {
        if (open) {
            setShiftId(existingAssignment?.shiftId ?? '');
            setEffectiveFrom(today());
            setEffectiveTo('');
            setOpenEnded(true);
            setReplaceExisting(existingAssignment != null);
            setErrors({});
        }
    }, [open, existingAssignment]);

    const validate = (): boolean => {
        const errs: typeof errors = {};
        if (!shiftId) errs.shiftId = t('assignmentEdit.shiftRequired');
        if (!effectiveFrom) errs.effectiveFrom = t('assignmentEdit.effectiveFromRequired');
        if (!openEnded && effectiveTo && effectiveTo < effectiveFrom) {
            errs.effectiveTo = t('assignmentEdit.effectiveToInvalid');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body: CreateAssignmentRequest = {
                userId,
                shiftId,
                effectiveFrom,
                effectiveTo: openEnded ? null : (effectiveTo || null),
                replaceExisting,
            };
            await ShiftAssignmentService.create(institutionId, body);
            toast.showSuccess(t('assignmentEdit.saved'));
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, t('assignmentEdit.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const activeShifts = shifts.filter(s => s.isActive);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('assignmentEdit.title', { userName })}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Stack spacing={2}>
                        <FormControl fullWidth required error={!!errors.shiftId}>
                            <InputLabel id="shift-select-label">{t('fields.shift')}</InputLabel>
                            <Select
                                labelId="shift-select-label"
                                value={shiftId}
                                label={t('fields.shift')}
                                onChange={e => { setShiftId(e.target.value); setErrors(prev => ({ ...prev, shiftId: undefined })); }}
                                inputProps={{ 'data-testid': 'assignment-shift-select' }}
                            >
                                {activeShifts.map(s => (
                                    <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>
                                ))}
                            </Select>
                            {errors.shiftId && <FormHelperText>{errors.shiftId}</FormHelperText>}
                        </FormControl>

                        <TextField
                            label={t('fields.effectiveFrom')}
                            type="date"
                            required
                            fullWidth
                            value={effectiveFrom}
                            onChange={e => { setEffectiveFrom(e.target.value); setErrors(prev => ({ ...prev, effectiveFrom: undefined })); }}
                            error={!!errors.effectiveFrom}
                            helperText={errors.effectiveFrom}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'assignment-effective-from' }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={openEnded}
                                    onChange={e => setOpenEnded(e.target.checked)}
                                    data-testid="assignment-open-ended"
                                />
                            }
                            label={t('assignmentEdit.openEnded')}
                        />

                        {!openEnded && (
                            <TextField
                                label={t('fields.effectiveTo')}
                                type="date"
                                fullWidth
                                value={effectiveTo}
                                onChange={e => { setEffectiveTo(e.target.value); setErrors(prev => ({ ...prev, effectiveTo: undefined })); }}
                                error={!!errors.effectiveTo}
                                helperText={errors.effectiveTo}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'data-testid': 'assignment-effective-to' }}
                            />
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={replaceExisting}
                                    onChange={e => setReplaceExisting(e.target.checked)}
                                    data-testid="assignment-replace-existing"
                                />
                            }
                            label={t('assignmentEdit.replaceExisting')}
                        />
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving} data-testid="assignment-save-btn">
                    {saving ? t('saving') : t('common:actions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignmentEditDialog;
